/*
 * Copyright (c) 2026 GS Elektromedizinische Geraete G. Stemple GmbH, Germany - all rights reserved.
 * <p>
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 */

import globalStyle from '../../../global.scss?inline';
import style from './ScoreButtonDropdown.scss?inline';
import { Autocomplete, InputAdornment, ListItem, ListItemButton, TextField, Typography } from '@mui/material';
import { useCSS } from '../../../provider/CSSProvider';
import { convertToRomanNumber } from '../../../utils/util';
import { useState } from 'react';

export type ScoreButtonLabel = string;

interface ScoreButtonDropdownProps<T> {
  title: string;
  listOfOptions: ReadonlyArray<readonly [T, ScoreButtonLabel]>;
  selectedValue: T | null;
  /**
   * The value to highlight, as default the selected value is highlighted
   */
  highlightedValue?: T | null;
  /**
   * When true, roman scores are displayed.
   */
  romanScores?: boolean;
  onChange: (value: T | null) => void;
}
function ScoreButtonDropdown<T>(props: ScoreButtonDropdownProps<T>) {
  useCSS(style);
  useCSS(globalStyle);

  const { title, listOfOptions, selectedValue, highlightedValue, romanScores, onChange } = props;

  const [autocompleteOpen, setAutocompleteOpen] = useState<boolean>(false);

  function getScoreLabel(score: number): string {
    if (romanScores) {
      return convertToRomanNumber(score);
    } else {
      return score.toString();
    }
  }

  function getListItem(value: T, label: string) {
    return (
      <ListItemButton
        key={label}
        className='score-button-dropdown-item'
        selected={value === (highlightedValue ?? selectedValue)}
        onClick={() => setAutocompleteOpen(true)}>
        <div className='option'>
          <div className='score'>{getScoreLabel(Number(value) + 1)}</div>
          <div className='label'>{label}</div>
        </div>
      </ListItemButton>
    );
  }

  return (
    <div className='score-button-dropdown add-margin-top-small'>
      <Autocomplete
        // Multiple=true as workaround for https://github.com/mui/material-ui/issues/26440
        multiple={true}
        open={autocompleteOpen}
        onOpen={() => setAutocompleteOpen(true)}
        onClose={() => setAutocompleteOpen(false)}
        options={listOfOptions}
        value={listOfOptions.filter(([value]) => value === selectedValue)}
        getOptionLabel={([, label]) => label}
        onChange={(_, value, reason) => {
          // Workaround for multiple=true
          if (value && value.length > 0) {
            const [newValue] = value[value.length - 1];
            onChange(newValue);
          } else {
            // do not remove option on reselect
            if (reason === 'clear') {
              onChange(null);
            }
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant='outlined'
            placeholder={selectedValue === null ? 'wählen...' : ''}
            slotProps={{
              input: {
                ...params.InputProps,
                startAdornment: (
                  <>
                    <InputAdornment position='start'>
                      <Typography align='left' className='title'>
                        {title}
                      </Typography>
                    </InputAdornment>
                    {/* Pass through as workaround for https://github.com/mui/material-ui/issues/22103 */}
                    {params.InputProps.startAdornment}
                  </>
                ),
              },
            }}
          />
        )}
        renderOption={(props, [value, label]) => (
          <ListItem {...props} key={label}>
            {getListItem(value, label)}
          </ListItem>
        )}
        renderTags={(tagValue) => {
          return tagValue.map(([value, label]) => {
            return getListItem(value, label);
          });
        }}></Autocomplete>
    </div>
  );
}

export default ScoreButtonDropdown;
